import { useState } from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import api from '../api';

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', subject: 'General enquiry', message: '' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/contact', form);
      toast({ title: 'Thank you — your note is with us', description: 'We reply to every message within two working days.' });
      setForm({ name: '', email: '', subject: 'General enquiry', message: '' });
    } catch (err) {
      toast({ title: 'Could not send', description: err.response?.data?.detail || 'Please try again' });
    } finally { setBusy(false); }
  };

  return (
    <main>
      <section className="border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <p className="text-[11px] tracking-widest-plus uppercase text-[#C6410E] mb-4">Contact</p>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-tight max-w-3xl">Write to us.<br/>We reply.</h1>
          <p className="text-[#1a1a1a]/70 mt-6 max-w-xl">Questions on sizing, colour, gifting, or repair — a small team reads every message and writes back within two working days.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-14">
        <aside className="space-y-10">
          <div>
            <p className="text-[11px] tracking-widest-plus uppercase mb-3 text-[#C6410E]">The Workshop</p>
            <div className="flex gap-3 items-start">
              <MapPin className="w-5 h-5 mt-1 text-[#1a1a1a]/50" />
              <p className="text-sm leading-relaxed">The BareBasics Studio<br/>LG-11, Dadabhagwan Complex<br/>Kamrej Char Rasta, Surat 394185<br/>Gujarat, India</p>
            </div>
          </div>
          <div>
            <p className="text-[11px] tracking-widest-plus uppercase mb-3 text-[#C6410E]">Write</p>
            <div className="flex gap-3 items-center">
              <Mail className="w-5 h-5 text-[#1a1a1a]/50" />
              <a href="mailto:studio@barebasics.co" className="text-sm link-underline">studio@barebasics.co</a>
            </div>
          </div>
          <div>
            <p className="text-[11px] tracking-widest-plus uppercase mb-3 text-[#C6410E]">Call</p>
            <div className="flex gap-3 items-center">
              <Phone className="w-5 h-5 text-[#1a1a1a]/50" />
              <a href="tel:+919712338381" className="text-sm link-underline">+91 97123 38381</a>
            </div>
            <p className="text-xs text-[#1a1a1a]/50 mt-2">Mon–Sat, 10 am to 6 pm IST</p>
          </div>
          <div>
            <p className="text-[11px] tracking-widest-plus uppercase mb-3 text-[#C6410E]">Visit</p>
            <p className="text-sm leading-relaxed text-[#1a1a1a]/75">Our studio welcomes visitors by appointment. Please write to us to arrange a time.</p>
          </div>
        </aside>

        <form onSubmit={submit} className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-1">
            <label className="text-[11px] tracking-widest-plus uppercase block mb-2">Your name</label>
            <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full bg-transparent border-b border-black/30 focus:border-[#C6410E] outline-none py-2 text-sm transition-colors" />
          </div>
          <div className="md:col-span-1">
            <label className="text-[11px] tracking-widest-plus uppercase block mb-2">Email</label>
            <input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full bg-transparent border-b border-black/30 focus:border-[#C6410E] outline-none py-2 text-sm transition-colors" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[11px] tracking-widest-plus uppercase block mb-2">Subject</label>
            <select value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} className="w-full bg-transparent border-b border-black/30 focus:border-[#C6410E] outline-none py-2 text-sm transition-colors">
              <option>General enquiry</option>
              <option>Product question</option>
              <option>Custom / made-to-order</option>
              <option>Repair &amp; care</option>
              <option>Press</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-[11px] tracking-widest-plus uppercase block mb-2">Message</label>
            <textarea required rows={6} value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="w-full bg-transparent border-b border-black/30 focus:border-[#C6410E] outline-none py-2 text-sm resize-none transition-colors" />
          </div>
          <div className="md:col-span-2">
            <button disabled={busy} type="submit" className="btn-primary">{busy ? 'Sending…' : 'Send message'}</button>
          </div>
        </form>
      </section>
    </main>
  );
}
